// Build and Pre-rendering Detection Utility
// This utility helps detect when the app is running during build/pre-rendering
// and prevents API calls that would cause timeouts

class BuildDetection {
    constructor() {
        // Set global flag immediately for other modules to check
        if (typeof window !== 'undefined') {
            window.__BUILD_DETECTION_ACTIVE__ = true;
        }
        
        this.isBuildTime = this.detectBuildEnvironment();
        
        // Set another global flag based on detection result
        if (typeof window !== 'undefined') {
            window.__IS_BUILD_TIME__ = this.isBuildTime;
        }
    }

    // Comprehensive build/pre-rendering detection
    detectBuildEnvironment() {
        // Immediate return for server-side or missing window
        if (typeof window === 'undefined') {
            return true;
        }

        if (typeof navigator === 'undefined') {
            return true;
        }

        // Check for pre-rendering user agents (most reliable method)
        const userAgent = window.navigator.userAgent || '';
        const preRenderingAgents = [
            'HeadlessChrome',
            'Chrome-Lighthouse', 
            'puppeteer',
            'react-snap',
            'prerender',
            'PhantomJS'
        ];

        for (const agent of preRenderingAgents) {
            if (userAgent.includes(agent)) {
                return true;
            }
        }

        // Check for pre-rendering flags
        if (window.__PRERENDER_INJECTED || window.__REACT_SNAP__) {
            return true;
        }

        // Check if requestAnimationFrame is missing (common in build environments)
        if (!window.requestAnimationFrame) {
            return true;
        }

        // Check for test environment
        if (process.env.NODE_ENV === 'test') {
            return true;
        }

        // Check if we're in a build process (no DOM interaction capabilities)
        try {
            if (!window.document || !window.document.createElement) {
                return true;
            }
        } catch (error) {
            return true;
        }

        // Additional check: see if we can access localStorage (often restricted in pre-rendering)
        try {
            window.localStorage.getItem('test');
        } catch (error) {
            return true;
        }

        // Check if we're running in a headless environment by testing for common headless indicators
        if (window.outerHeight === 0 || window.outerWidth === 0) {
            return true;
        }

        return false;
    }

    // Check if API calls should be skipped
    shouldSkipApiCalls() {
        return this.isBuildTime;
    }

    // Log detection result for debugging
    logDetectionResult() {
        // Removed console logs for production
    }
}

// Create singleton instance immediately
const buildDetection = new BuildDetection();

// Set global function for immediate checking
if (typeof window !== 'undefined') {
    window.shouldSkipApiCalls = () => buildDetection.shouldSkipApiCalls();
}

// Log the detection result
buildDetection.logDetectionResult();

export default buildDetection;